/* eslint-disable react/no-danger */


import React, { ReactElement } from 'react';
import { connect, useSelector } from 'react-redux';
import t from 'react-translate';
import { isEmpty } from 'underscore';

// Helpers
import { i18nJoin, JoinMode } from 'shared/services/i18n-utils';

// Schemas
import { RootState } from 'redux/schemas';
import { Communication, CommunicationType, CommunicationErrorCode, Recipient, HasLoggedInFilterType, ItemState } from 'redux/schemas/models/course-communication';

// Selectors
import { getCommunication, getItemState, getRolesFilterTitles } from 'redux/selectors/course-communications';
import { getCourseAliases } from 'redux/selectors/course';
import { getPrefixedTitle } from 'redux/selectors/activity';

// Components
import NvRoleBadge from 'shared/components/nv-role-badge';
import { getRecipientLabel, getRecipientsLabel, orderedRecipients } from 'course_communications/components/communication-modal/send-to';
import CommunicationRow, { transformFirstCharacterToLowerCase, transformToErrorTitle, transformToActivityTitle } from './communication-row';

type CommunicationFilterRowProps = {
  communicationId?: number,
  showOwnerActivity?: boolean,
};

type StateProps = {
  communication: Communication,
  rolesFilterTitles: string[],
  itemState: ItemState,
};

export const CommunicationFilterRow = (props: CommunicationFilterRowProps & StateProps) => {
  const aliases = useSelector((state) => getCourseAliases(state));

  // Need to pass aliases, so using useSelector pattern
  // Getting the actvity titles with prefix
  const ownerActivityTitle = useSelector((state) => getPrefixedTitle(
    state,
    props.communication.ownerType,
    props.communication.ownerId,
    aliases,
  ));
  const hasCompletedActivityTitle = useSelector((state) => getPrefixedTitle(
    state,
    props.communication.filters?.hasCompleted?.type,
    props.communication.filters?.hasCompleted?.id,
    aliases,
  ));
  const hasNotCompletedActivityTitle = useSelector((state) => getPrefixedTitle(
    state,
    props.communication.filters?.hasNotCompleted?.type,
    props.communication.filters?.hasNotCompleted?.id,
    aliases,
  ));

  const filters = [];
  const isSent = props.itemState === ItemState.SENT;

  if (props.communication?.communicationType === CommunicationType.NOT_COMPLETED) {
    let title;

    if (props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_THIRD_PARTY_COMPLETION)
      || props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_FEEDBACK_COMPLETION)) {
      title = transformToErrorTitle(
        t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY,
        CommunicationErrorCode.UNABLE_TO_TRACK_THIRD_PARTY_COMPLETION,
        aliases,
        isSent,
        {
          activity: !props.showOwnerActivity
            ? t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.THIS_ACTIVITY()
            : ownerActivityTitle,
        },
      );
    } else if (props.communication.errorCodes?.includes(CommunicationErrorCode.MISSING_PROFILE_COMPLETION_REQUIRED_QUESTIONS)) {
      title = transformToErrorTitle(
        t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY_QUESTION_MISSING,
        CommunicationErrorCode.MISSING_PROFILE_COMPLETION_REQUIRED_QUESTIONS,
        aliases,
        isSent,
        {
          activity: !props.showOwnerActivity
            ? t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.THIS_ACTIVITY()
            : ownerActivityTitle,
        },
      );
    } else {
      title = transformToActivityTitle(
        t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY,
        ownerActivityTitle,
        !props.showOwnerActivity,
        true,
      );
    }

    filters.push({
      key: 'hasNotCompletedType',
      title,
    });
  }

  if (props.communication?.filters) {
    // has No Roles, Catalyst Roles or Team Roles
    if (props.communication.filters.courseRolesList) {
      // Reduce through role titles to get comma-separated(,|or) text
      let hasError: boolean = false;

      const rolesText = i18nJoin(
        props.rolesFilterTitles.map((title) => {
          let titleText: string;

          // Role missing error will be for whole communication. If the title is
          // not present that means this role is missing
          if (!title) {
            hasError = true;
            // Injecting a string here, so that it will be used to
            // split the text later to inject the error.
            titleText = '{ROLE_MISSING_ERROR}';
          } else {
            titleText = title === 'NO_ROLE'
              ? t.COURSE_COMMUNICATIONS.FILTERS.ROLES.NO_ROLES()
              : t.COURSE_COMMUNICATIONS.FILTERS.ROLES.TITLE(title);
          }

          return titleText;
        }),
        JoinMode.OR,
      );

      let title: ReactElement | string;
      if (hasError) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.ROLES.DISPLAY,
          CommunicationErrorCode.COURSE_ROLE_DELETED_FILTER_ERROR,
          aliases,
          isSent,
          { rolesText },
        );
      } else {
        title = transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.ROLES.DISPLAY(rolesText));
      }

      filters.push({
        key: 'courseRolesList',
        title,
      });
    }

    // enrolled in last X days
    if (props.communication.filters.enrolledInLastXDays) {
      filters.push({
        key: 'enrolledInLastXDays',
        title: transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.ENROLLED_LAST_DAYS.LABEL(props.communication.filters.enrolledInLastXDays.toString())),
      });
    }

    // has been active at least once in the last X days
    if (props.communication.filters.daysActive) {
      filters.push({
        key: 'daysActive',
        title: transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.ACTIVE_DAYS.ACTIVE.LABEL(props.communication.filters.daysActive.toString())),
      });
    }

    // has been inactive for more than X days
    if (props.communication.filters.daysInactive) {
      filters.push({
        key: 'daysInactive',
        title: transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.ACTIVE_DAYS.INACTIVE.LABEL(props.communication.filters.daysInactive.toString())),
      });
    }

    // has completed <b>VIDEO : Title</b>
    if (props.communication.filters.hasCompleted) {
      let title: ReactElement | string;
      if (props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_COMPLETED_ACTIVITY_FEEDBACK_COMPLETION)
        || props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_COMPLETED_ACTIVITY_THIRD_PARTY_COMPLETION)) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_WITH_ACTIVITY,
          CommunicationErrorCode.UNABLE_TO_TRACK_COMPLETED_ACTIVITY_FEEDBACK_COMPLETION,
          aliases,
          isSent,
        );
      } else if (props.communication.errorCodes?.includes(CommunicationErrorCode.MISSING_COMPLETED_PROFILE_COMPLETION_REQUIRED_QUESTIONS)
        && hasCompletedActivityTitle) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_WITH_ACTIVITY_QUESTION_MISSING,
          CommunicationErrorCode.MISSING_COMPLETED_PROFILE_COMPLETION_REQUIRED_QUESTIONS,
          aliases,
          isSent,
          {
            activity: hasCompletedActivityTitle,
          },
        );
      } else if (props.communication.errorCodes?.includes(CommunicationErrorCode.MISSING_COMPLETED_ACTIVITY_FILTER_ERROR)
        || !hasCompletedActivityTitle) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_WITH_ACTIVITY,
          CommunicationErrorCode.MISSING_COMPLETED_ACTIVITY_FILTER_ERROR,
          aliases,
          isSent,
        );
      } else {
        title = transformToActivityTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_WITH_ACTIVITY,
          hasCompletedActivityTitle,
          false,
          true,
        );
      }

      filters.push({
        key: 'hasCompleted',
        title,
      });
    }

    // has not completed <b>VIDEO : Title</b>
    if (props.communication.filters.hasNotCompleted) {
      let title: ReactElement | string;
      if (props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_NOT_COMPLETED_ACTIVITY_FEEDBACK_COMPLETION)
        || props.communication.errorCodes?.includes(CommunicationErrorCode.UNABLE_TO_TRACK_NOT_COMPLETED_THIRD_PARTY_COMPLETION)) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY,
          CommunicationErrorCode.UNABLE_TO_TRACK_NOT_COMPLETED_ACTIVITY_FEEDBACK_COMPLETION,
          aliases,
          isSent,
        );
      } else if (props.communication.errorCodes?.includes(CommunicationErrorCode.MISSING_NOT_COMPLETED_PROFILE_COMPLETION_REQUIRED_QUESTIONS)
        && hasNotCompletedActivityTitle) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY_QUESTION_MISSING,
          CommunicationErrorCode.MISSING_NOT_COMPLETED_PROFILE_COMPLETION_REQUIRED_QUESTIONS,
          aliases,
          isSent,
          {
            activity: hasNotCompletedActivityTitle,
          },
        );
      } else if (props.communication.errorCodes?.includes(CommunicationErrorCode.MISSING_NOT_COMPLETED_ACTIVITY_FILTER_ERROR)
        || !hasNotCompletedActivityTitle) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY,
          CommunicationErrorCode.MISSING_NOT_COMPLETED_ACTIVITY_FILTER_ERROR,
          aliases,
          isSent,
        );
      } else {
        title = transformToActivityTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ACTIVITY.HAS_NOT_WITH_ACTIVITY,
          hasNotCompletedActivityTitle,
          false,
          true,
        );
      }

      filters.push({
        key: 'hasNotCompleted',
        title,
      });
    }

    // has earned more than X points
    if (props.communication.filters.earnedMoreThanXPoints) {
      let title: ReactElement | string;
      if (props.communication.errorCodes?.includes(CommunicationErrorCode.COURSE_POINTS_DISABLED)) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.EARNED_POINTS.MORE_THAN.LABEL,
          CommunicationErrorCode.COURSE_POINTS_DISABLED,
          aliases,
          isSent,
          { input: props.communication.filters.earnedMoreThanXPoints },
        );
      } else {
        title = transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.EARNED_POINTS.MORE_THAN.LABEL({
          input: props.communication.filters.earnedMoreThanXPoints,
          ...aliases.pointsAliases,
        }));
      }

      filters.push({
        key: 'earnedMoreThanXPoints',
        title,
      });
    }

    // has earned less than X points
    if (props.communication.filters.earnedLessThanXPoints) {
      let title: ReactElement | string;
      if (props.communication.errorCodes?.includes(CommunicationErrorCode.COURSE_POINTS_DISABLED)) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.EARNED_POINTS.LESS_THAN.LABEL,
          CommunicationErrorCode.COURSE_POINTS_DISABLED,
          aliases,
          isSent,
          { input: props.communication.filters.earnedLessThanXPoints },
        );
      } else {
        title = transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.EARNED_POINTS.LESS_THAN.LABEL({
          input: props.communication.filters.earnedLessThanXPoints,
          ...aliases.pointsAliases,
        }));
      }

      filters.push({
        key: 'earnedLessThanXPoints',
        title,
      });
    }

    // completed less than X number of assignments that are marked as To-dos
    if (props.communication.filters.completedXAssignmentsAsTodo) {
      let title: ReactElement | string;

      if (props.communication.errorCodes?.includes(CommunicationErrorCode.COURSE_NO_TODO_EXERCISES)) {
        title = transformToErrorTitle(
          t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ASSIGNMENTS.LABEL,
          CommunicationErrorCode.COURSE_NO_TODO_EXERCISES,
          aliases,
          isSent,
          { input: props.communication.filters.completedXAssignmentsAsTodo },
        );
      } else {
        title = transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_ASSIGNMENTS.LABEL({
          input: props.communication.filters.completedXAssignmentsAsTodo,
          ...aliases.assignmentAliases,
        }));
      }

      filters.push({
        key: 'completedXAssignmentsAsTodo',
        title,
      });
    }

    // has completed the course
    if (props.communication.filters.hasCompletedCourse === true) {
      filters.push({
        key: 'hasCompletedCourse',
        title: transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_COURSE.HAS(aliases.courseAliases)),
      });
    }

    // has NOT completed the course
    if (props.communication.filters.hasCompletedCourse === false) {
      filters.push({
        key: 'hasNotCompletedCourse',
        title: transformFirstCharacterToLowerCase(t.COURSE_COMMUNICATIONS.FILTERS.COMPLETED_COURSE.HAS_NOT(aliases.courseAliases)),
      });
    }

    // has logged in
    if (props.communication.filters.hasLoggedIn) {
      let title: string;
      if (props.communication.filters.hasLoggedIn === HasLoggedInFilterType.HAS_LOGGED_IN) {
        title = t.COURSE_COMMUNICATIONS.FILTERS.LOGGED_IN.HAS();
      } else if (props.communication.filters.hasLoggedIn === HasLoggedInFilterType.HAS_NOT_SINCE_START_DATE) {
        title = t.COURSE_COMMUNICATIONS.FILTERS.LOGGED_IN.HAS_NOT_COURSE_DATE(aliases.courseAliases);
      } else if (props.communication.filters.hasLoggedIn === HasLoggedInFilterType.HAS_NOT_SINCE_RELEASE_DATE) {
        title = t.COURSE_COMMUNICATIONS.FILTERS.LOGGED_IN.HAS_NOT_RELEASE_DATE(aliases.courseAliases);
      }

      filters.push({
        key: 'hasLoggedIn',
        title: transformFirstCharacterToLowerCase(title),
      });
    }
  }

  // No need to display filters for trigger type
  const showFilterRow = filters.length > 0
    || !(props.communication?.communicationType === CommunicationType.COMPLETED
      || props.communication?.communicationType === CommunicationType.NOT_COMPLETED);

  let filterRowDescription: string = '';

  const recipients = props.communication?.recipients && !isEmpty(props.communication.recipients)
    ? orderedRecipients(props.communication.recipients)
    : [Recipient.TRIGGER_LEARNER]; // To handle trigger learner recipient

  if (showFilterRow) {
    const recipientTitle = getRecipientsLabel(recipients, aliases);

    filterRowDescription = filters.length > 0
      ? t.COURSE_COMMUNICATIONS.FILTERS.FILTER_ROW_DESCRIPTION(recipientTitle)
      : recipientTitle;
  }

  return (
    <React.Fragment>
      {showFilterRow && (
        <CommunicationRow>
          <div className='content-wrapper'>
            <div className='icons-wrapper recipient-icons'>
              {/* The role badges are displyed right to left , so reversing it */}
              { recipients && recipients.reverse().map((recipient) => (
                <NvRoleBadge key={recipient} recipient={recipient} label={getRecipientLabel(recipient, aliases)} />
              ))}
            </div>
            <div className='content text-medium'>
              <div className='title'>
                { showFilterRow && filterRowDescription }
              </div>
              { filters.map((filter) => (
                <React.Fragment key={filter.key}>
                  { // string means its a translated string which may contain html,
                    // or else treat it as a ReactElement => titles with warnings
                    typeof filter.title === 'string'
                      ? (
                        <div
                          className='title'
                          dangerouslySetInnerHTML={{
                            __html: filter.title,
                          }}
                        />
                      )
                      : (
                        <div className='title'>{filter.title}</div>
                      )
                  }
                </React.Fragment>
              ))}
            </div>
          </div>
        </CommunicationRow>
      )}
    </React.Fragment>
  );
};

const ConnectedCommunicationFilterRow = connect(
  (state: RootState, ownProps: CommunicationFilterRowProps) => ({
    communication: getCommunication(state, ownProps.communicationId),
    rolesFilterTitles: getRolesFilterTitles(state, ownProps.communicationId),
    itemState: getItemState(state, ownProps.communicationId),
  }),
)(CommunicationFilterRow);

export default ConnectedCommunicationFilterRow;
